import React from "react";
import { money } from "@/components/common";
import { Plus, X } from "@phosphor-icons/react";

const num = (v) => Number(v) || 0;
export const INVOICEABLE = ["in transit", "arrived at receiver", "delivered"];

export function dispatchCharge(load, rate) {
  if (num(load.dispatch_charges) > 0) return num(load.dispatch_charges);
  const txt = String(rate || "").replace(/,/g, "");
  const m = txt.match(/(\d+(?:\.\d+)?)/);
  if (!m) return 0;
  const v = Number(m[1]);
  const pct = txt.includes("%") || (!txt.includes("$") && v <= 30);
  return pct ? Math.round(num(load.rate) * v) / 100 : v;
}

const miles = (l) => num(l.total_miles) || num(l.dead_head_miles) + num(l.loaded_miles);

export const loadItem = (l, product, rate) => ({
  ref: `load:${l.id}`, quantity: 1,
  description: `Load #${l.po_number || l.reference || "—"} · ${l.origin || "?"} → ${l.destination || "?"}`,
  detail: [l.pickup_date && `Pickup ${l.pickup_date}`, miles(l) && `${miles(l)} mi`, `Load amount ${money(l.rate)}`, product === "Dispatch" && rate && `Dispatch rate ${rate}`, l.driver_name && `Driver ${l.driver_name}`].filter(Boolean).join(" · "),
  rate: product === "Dispatch" ? dispatchCharge(l, rate) : 0,
});

export const rentalItem = (r) => ({
  ref: `rental:${r.id}`, quantity: 1,
  description: `Trailer Rent · Trailer #${r.trailer_number || "—"}${r.trailer_type ? ` · ${r.trailer_type}` : ""}`,
  detail: [r.vin && `VIN ${r.vin}`, r.next_due && `Next due ${r.next_due}`, r.renting_company, `${r.period_months} mo term`].filter(Boolean).join(" · "),
  rate: num(r.rent_amount),
});

export function PickTable({ title, rows, isPicked, onToggle, columns, empty, testid }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{title}</p>
      {rows.length === 0 ? <p className="text-sm text-slate-400 py-2" data-testid={`${testid}-empty`}>{empty}</p> : (
        <div className="rounded-lg border border-slate-200 overflow-x-auto max-h-60 overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0"><tr><th className="px-2 py-2 w-8" />{columns.map(([h, , right]) => <th key={h} className={`px-2 py-2 ${right ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onToggle(r)} className={`cursor-pointer ${isPicked(r) ? "bg-blue-50/70" : "hover:bg-slate-50"}`} data-testid={`${testid}-${r.id}`}>
                  <td className="px-2 py-2 text-center"><input type="checkbox" readOnly checked={isPicked(r)} className="pointer-events-none accent-blue-600" /></td>
                  {columns.map(([h, render, right]) => <td key={h} className={`px-2 py-2 ${right ? "text-right font-mono" : "text-slate-700"}`}>{render(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function LineItems({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const total = items.reduce((s, it) => s + num(it.quantity) * num(it.rate), 0);
  const cls = "h-9 rounded-md border border-slate-300 bg-white px-2 text-sm";
  return (
    <div data-testid="inv-line-items">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Invoice Line Items</p>
        <button type="button" onClick={() => onChange([...items, { ref: `manual:${Date.now()}`, description: "", detail: "", quantity: 1, rate: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700" data-testid="inv-add-line"><Plus size={13} weight="bold" />Add line</button>
      </div>
      {items.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg" data-testid="inv-no-items">Pick loads or rentals above, or add a line by hand.</p> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.ref} className="grid grid-cols-[1fr_64px_100px_90px_24px] gap-2 items-start" data-testid={`inv-item-${i}`}>
              <div className="space-y-1">
                <input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" className={`${cls} w-full font-semibold`} data-testid={`inv-item-${i}-desc`} />
                <input value={it.detail || ""} onChange={(e) => upd(i, "detail", e.target.value)} placeholder="Details (optional)" className="w-full h-7 px-2 text-xs text-slate-500 bg-transparent border-b border-dashed border-slate-200 focus:outline-none focus:border-blue-400" />
              </div>
              <input type="number" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className={`${cls} font-mono text-right`} data-testid={`inv-item-${i}-qty`} />
              <input type="number" value={it.rate} onChange={(e) => upd(i, "rate", e.target.value)} placeholder="0.00" className={`${cls} font-mono text-right`} data-testid={`inv-item-${i}-rate`} />
              <span className="h-9 flex items-center justify-end font-mono font-semibold text-slate-900 text-sm">{money(num(it.quantity) * num(it.rate))}</span>
              <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="h-9 text-slate-400 hover:text-red-500" data-testid={`inv-item-${i}-remove`}><X size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end mt-3 text-sm"><span className="text-slate-500 mr-3">Invoice total</span><span className="font-mono font-extrabold text-lg text-slate-900" data-testid="inv-total">{money(total)}</span></div>
    </div>
  );
}
